import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { flowRight as compose } from 'lodash';
import { connect } from 'react-redux';
import {
  Grid,
  Row,
  Col,
  Badge
} from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

// Component
import InboxItem from './InboxItem';
import EmptyInbox from './EmptyInbox';
import Loader from '../Loader';
import CustomPagination from '../CustomPagination';

// helpers 
import UnreadCountQuery from './UnreadCount.graphql';
import GetAllThreadQuery from './AllThreadsQuery.graphql';
import messages from '../../locale/messages';
import { withQuery } from '../../hoc/withGraphql';

//style
import s from './Inbox.css';
import showToaster from '../../helpers/showToaster';

const Inbox = ({
  allThreads: { refetch, loading, GetAllThreads },
  inboxLoader, UnreadCount: { getUnreadCount } }) => {

  const { formatMessage } = useIntl();
  const [currentPage, setCurrentPage] = useState(1);
  const [type, setType] = useState('guest');

  useEffect(() => {
    if (inboxLoader) {
      setCurrentPage(1);
      refetch({ currentPage: 1, type });
    }
  }, [inboxLoader, refetch])

  useEffect(() => {
    
    // Check for error parameter in URL and show toast
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get('error');
      if (error === 'payment_failed') {
        showToaster({ 
          messageId: 'paymentFailedError', 
          toasterType: 'error' 
        });
        // Clean up URL by removing the error parameter
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  

  }, [])

  const changeType = (threadType) => {
    setCurrentPage(1);
    setType(threadType);
    refetch({ threadType, currentPage: 1 });
  }

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const paginationData = (page) => {
    refetch({ currentPage: page });
    scrollTop();
    setCurrentPage(page);
  }

  let hostActive, guestActive, host, guest, hostActiveDark, guestActiveDark;
  if (type === 'host') {
    hostActive = s.active;
  } else {
    guestActive = s.active;
  }
  if (type === 'host') {
    hostActiveDark = 'activeDark';
  } else {
    guestActiveDark = 'activeDark';
  }
  if (getUnreadCount) {
    host = getUnreadCount.hostCount != null && getUnreadCount.hostCount > 0 ? getUnreadCount.hostCount : null;
    guest = getUnreadCount.guestCount != null && getUnreadCount.guestCount > 0 ? getUnreadCount.guestCount : null;
  }

  return (
    <Grid fluid>
      <Row>
        <Col xs={12} sm={12} md={12} lg={12}>
          <div className={'tripContainer inboxContainer'}>
            <div className={cx(s.listTabFlex, s.space5)}>
              <div className={cx(guestActive, guestActiveDark)}>
                <a
                  className={cx(s.tabItem, 'inboxBorder', guestActive, guestActiveDark)}
                  href="javascript:void(0)"
                  onClick={() => changeType('guest')}
                >
                  <FormattedMessage {...messages.traveling} />
                  {
                    guest != null && <Badge
                      className={cx(s.count, 'countRTL')}
                    >
                      {guest}
                    </Badge>
                  }
                </a>
              </div>
              <div className={cx(hostActive, hostActiveDark)}>
                <a
                  className={cx(s.tabItem, 'inboxBorder')}
                  href="javascript:void(0)"
                  onClick={() => changeType('host')}
                >
                  <FormattedMessage {...messages.hosting} />
                  {
                    host != null && <Badge
                      className={cx(s.count, 'countRTL')}
                    >
                      {host}
                    </Badge>
                  }
                </a>
              </div>
            </div>
            <>
              {
                loading && <Loader type={"text"} />
              }

              {
                !loading && GetAllThreads && GetAllThreads.threadsData && GetAllThreads.threadsData.length > 0
                && <ul className={cx(s.listLayout, 'listLayoutArbic')}>
                  {
                    GetAllThreads.threadsData.map((item, index) => {
                      if (item.guestProfile && item.hostProfile) {
                        return <InboxItem
                          key={index}
                          threadId={item.id}
                          type={type}
                          profileId={type === 'host' ? item.guestProfile.profileId : item.hostProfile.profileId}
                          picture={type === 'host' ? item.guestProfile.picture : item.hostProfile.picture}
                          displayName={type === 'host' ? item.guestProfile.firstName : item.hostProfile.firstName}
                          isPhotoMigrated={type === 'host' ? item.guestProfile.isMigrated : item.hostProfile.isMigrated}
                          content={item?.threadItem?.content}
                          createdAt={item?.threadItem?.createdAt}
                          city={item?.listData?.city}
                          state={item.listData?.state}
                          country={item.listData?.country}
                          startDate={item?.threadItem?.startDate}
                          endDate={item?.threadItem?.endDate}
                          status={item?.threadItem?.type}
                          sentBy={item.threadItem?.sentBy}
                          read={item.threadItem?.isRead}
                          paymentState={item?.reservation?.paymentState}
                        />
                      } else {
                        return <li key={index}> </li>
                      }
                    })
                  }
                </ul>
              }
              {
                !loading && GetAllThreads && GetAllThreads.threadsData && GetAllThreads.threadsData.length === 0
                && <EmptyInbox type={type} />
              }

              {
                GetAllThreads && GetAllThreads.threadsData && GetAllThreads.threadsData.length > 0
                && <div>
                  <CustomPagination
                    total={GetAllThreads.count}
                    currentPage={currentPage}
                    defaultCurrent={1}
                    defaultPageSize={5}
                    change={paginationData}
                    paginationLabel={formatMessage(messages.messages)}
                  />
                </div>
              }
            </>
          </div>
        </Col>
      </Row>
    </Grid>
  );
}

Inbox.propTypes = {
  formatMessage: PropTypes.any,
  allThreads: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    GetAllThreads: PropTypes.shape({
      count: PropTypes.number.isRequired,
      threadsData: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        listData: PropTypes.shape({
          city: PropTypes.string.isRequired,
          state: PropTypes.string.isRequired,
          country: PropTypes.string.isRequired,
        }),
        guestProfile: PropTypes.shape({
          profileId: PropTypes.number.isRequired,
          picture: PropTypes.string,
          displayName: PropTypes.string.isRequired,
        }),
        hostProfile: PropTypes.shape({
          profileId: PropTypes.number.isRequired,
          picture: PropTypes.string,
          displayName: PropTypes.string.isRequired,
        }),
        threadItem: PropTypes.shape({
          type: PropTypes.string.isRequired,
          content: PropTypes.string,
          startDate: PropTypes.string,
          endDate: PropTypes.string,
          isRead: PropTypes.bool.isRequired,
          sentBy: PropTypes.string.isRequired,
          createdAt: PropTypes.string.isRequired,
        })
      }))
    }),
  }),
  UnreadCount: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    getUnreadCount: PropTypes.shape({
      hostCount: PropTypes.number.isRequired,
      guestCount: PropTypes.number.isRequired
    })
  })
};

Inbox.defaultProps = {
  allThreads: {
    loading: true,
    GetAllThreads: {
      count: null,
      threadsData: []
    }
  },
  UnreadCount: {
    loading: true,
    hostCount: null,
    guestCount: null
  }
};

const mapState = (state) => ({
  inboxLoader: state.loader.inbox,
});

export default compose(
  withStyles(s),
  connect(mapState),
  withQuery(UnreadCountQuery, {
    name: 'UnreadCount',
    options: {
      ssr: false,
      fetchPolicy: 'network-only'
    }
  }),
  withQuery(GetAllThreadQuery, {
    name: 'GetAllThreads',
    options: {
      ssr: false,
      fetchPolicy: 'network-only',
    }
  })
)(Inbox);